import { useState, FormEvent } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import JsonLd from "../../components/feature/JsonLd";

const FORM_URL = "https://readdy.ai/api/form/d72qp2cmc4tbhfnc6k00";
const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? "";

const contactSchema: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "Contact The Magazine Co.",
  url: `${siteUrl}/contact`,
  description: "Contact The Magazine Co. to discuss turning your content into a premium print magazine.",
  mainEntity: {
    "@type": "Organization",
    name: "The Magazine Co.",
    telephone: "+16786633900",
    email: "makeitamagazine@gmail.com",
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: "08:00",
        closes: "16:00",
      },
    ],
  },
};

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const message = data.get("message") as string;
    if (message && message.length > 500) return;
    setStatus("loading");
    const params = new URLSearchParams();
    data.forEach((value, key) => params.append(key, value as string));
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      if (res.ok) { setStatus("success"); form.reset(); setCharCount(0); }
      else setStatus("error");
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact The Magazine Co. | Let's Build Your Magazine Together"
        description="Get in touch with The Magazine Co. Tell us about your content and audience, and we'll help you launch your own print magazine. Response within 48 hours."
        keywords="contact magazine publisher, print magazine inquiry, magazine publishing consultation"
        path="/contact"
      />
      <JsonLd id="contact-schema" schema={contactSchema} />
      <Navbar />

      {/* ── Hero ── */}
      <div className="relative w-full h-[600px] overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/32a14513ec32f0e72e0fe17cf8d1a3f3.jpg"
          alt="Stack of magazines on a bookshelf at home"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6">
          <p className="text-[#F0EAD6] text-xs tracking-[0.35em] uppercase font-semibold mb-5">Get in Touch</p>
          <h1 className="text-5xl md:text-6xl font-serif font-light text-white leading-[1.1] mb-6">
            Let&apos;s Build Your<br /><em className="italic">Magazine Together</em>
          </h1>
          <p className="text-white/75 text-lg font-light max-w-xl leading-relaxed">
            Tell us about your content, your audience, and let us create the publication you&apos;ve never imagined.
          </p>
        </div>
      </div>

      {/* ── Main Content ── */}
      <section className="bg-[#F8F4EE] py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-5 gap-16">

          {/* Left column */}
          <div className="lg:col-span-2 flex flex-col gap-10">

            <div>
              <p className="text-[#C41E3A] text-xs tracking-[0.3em] uppercase font-semibold mb-5">Contact Info</p>
              <ul className="flex flex-col gap-6">
                {[
                  { icon: "ri-mail-line", label: "Email Us", value: "makeitamagazine@gmail.com" },
                  { icon: "ri-phone-line", label: "Call Us", value: "(678) 663-3900" },
                  { icon: "ri-time-line", label: "Office Hours", value: "Mon\u2013Fri, 8:00 AM \u2013 4:00 PM" },
                ].map((info) => (
                  <li key={info.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-[#C41E3A] w-5 h-5 flex items-center justify-center">
                        <i className={info.icon} />
                      </span>
                    </div>
                    <div>
                      <p className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold mb-0.5">{info.label}</p>
                      <p className="text-[#2B2B2B] text-sm leading-relaxed">{info.value}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#8B8B8B] font-semibold mb-4">Follow Us</p>
              <div className="flex items-center gap-3">
                {[
                  { icon: "ri-instagram-line", label: "Instagram" },
                  { icon: "ri-twitter-x-line", label: "Twitter" },
                  { icon: "ri-linkedin-line", label: "LinkedIn" },
                  { icon: "ri-pinterest-line", label: "Pinterest" },
                ].map((s) => (
                  <a key={s.label} href="#" aria-label={s.label} rel="nofollow"
                    className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-[#2B2B2B] hover:bg-[#C41E3A] hover:text-white transition-colors cursor-pointer">
                    <span className="w-5 h-5 flex items-center justify-center"><i className={s.icon} /></span>
                  </a>
                ))}
              </div>
            </div>

            <div className="bg-[#1C1C1C] rounded-2xl p-6 text-white">
              <p className="text-[#F0EAD6] text-xs tracking-widest uppercase font-semibold mb-3">Response Time</p>
              <p className="font-serif text-2xl mb-2">Within 48 Hours</p>
              <p className="text-white/60 text-sm leading-relaxed">
                Our creator team personally reviews every enquiry and will get back to you within 48 hours.
              </p>
            </div>

            <div className="w-full h-64 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=neatly%20stacked%20pile%20of%20glossy%20print%20magazines%20on%20a%20marble%20side%20table%20in%20a%20cozy%20living%20room%2C%20warm%20afternoon%20sunlight%2C%20cream%20and%20terracotta%20tones%2C%20home%20interior%20styling%2C%20editorial%20photo&width=700&height=500&seq=8836&orientation=landscape"
                alt="Stack of magazines at home"
                className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-700"
              />
            </div>

          </div>

          {/* Form column */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 md:p-10">
              <h2 className="font-serif text-3xl text-[#2B2B2B] mb-2">Send Us a Message</h2>
              <p className="text-[#8B8B8B] text-sm mb-8">Fill in your details and we&apos;ll reach out within 48 hours.</p>

              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-16 text-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-[#FFECEF] flex items-center justify-center">
                    <span className="text-[#C41E3A] text-2xl w-8 h-8 flex items-center justify-center">
                      <i className="ri-check-line" />
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl text-[#2B2B2B]">Message Received!</h3>
                  <p className="text-[#6B6B6B] text-sm max-w-sm">
                    Thank you for reaching out. Our creator team will be in touch within 48 hours.
                  </p>
                  <button onClick={() => setStatus("idle")}
                    className="mt-4 text-sm text-[#C41E3A] font-semibold hover:underline cursor-pointer">
                    Send another message
                  </button>
                </div>
              ) : (
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="first_name">
                        First Name <span className="text-[#C41E3A]">*</span>
                      </label>
                      <input id="first_name" name="first_name" type="text" required placeholder="Charlotte"
                        className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="last_name">
                        Last Name <span className="text-[#C41E3A]">*</span>
                      </label>
                      <input id="last_name" name="last_name" type="text" required placeholder="Blake"
                        className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors" />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="email">
                      Email Address <span className="text-[#C41E3A]">*</span>
                    </label>
                    <input id="email" name="email" type="email" required placeholder="you@example.com"
                      className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="content_niche">
                      Content Niche <span className="text-[#C41E3A]">*</span>
                    </label>
                    <input id="content_niche" name="content_niche" type="text" required
                      placeholder="e.g. Travel, Food, Architecture, Fashion..."
                      className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors" />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="distribution">
                      Preferred Distribution
                    </label>
                    <select id="distribution" name="distribution"
                      className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] focus:outline-none focus:border-[#C41E3A] transition-colors cursor-pointer">
                      <option value="">Select a frequency</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs tracking-widest uppercase text-[#8B8B8B] font-semibold" htmlFor="message">
                      Tell Us About Your Content <span className="text-[#C41E3A]">*</span>
                    </label>
                    <textarea id="message" name="message" required rows={5} maxLength={500}
                      placeholder="Describe your content, audience size, publishing goals, and anything else we should know..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-lg border border-[#E8E0D0] bg-[#FAFAF8] text-sm text-[#2B2B2B] placeholder:text-[#C0B8A8] focus:outline-none focus:border-[#C41E3A] transition-colors resize-none" />
                    <p className={`text-xs self-end ${charCount > 480 ? "text-[#C41E3A]" : "text-[#C0B8A8]"}`}>
                      {charCount}/500
                    </p>
                  </div>

                  {status === "error" && (
                    <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                      Something went wrong. Please try again or email us directly.
                    </div>
                  )}

                  <button type="submit" disabled={status === "loading"}
                    className="w-full py-4 rounded-full bg-[#C41E3A] text-white text-sm font-semibold tracking-wide whitespace-nowrap hover:bg-[#A0182E] transition-colors cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {status === "loading" ? (
                      <>
                        <span className="w-4 h-4 flex items-center justify-center animate-spin">
                          <i className="ri-loader-4-line" />
                        </span>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line" /></span>
                      </>
                    )}
                  </button>

                </form>
              )}
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}
